import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGuest } from "./GuestSystem";
import { Star, Gamepad2, BookOpen, Clock, Lock, X, Rocket, Compass } from "lucide-react";

/**
 * GuestCoach, smart coaching layer that nudges wandering guests toward
 * star-earning activities based on elapsed time + what they've actually done.
 *
 * Rules (first match wins, evaluated every tick):
 *   1. T-60s remaining              → HIGH: "1 min left, don't lose your stars!"
 *   2. 60s+ elapsed, 0 stars        → "Try tapping stars to catch them"
 *   3. 120s+ elapsed, 0 games       → "Play a game, win up to 5 stars!"
 *   4. 180s+ elapsed, <3 stars      → "Completing a lesson gives 3 bonus stars"
 *   5. 240s+ elapsed, <5 stars      → "Explore a world, find hidden stars!"
 *
 * Each tip fires at most once per guest session (tracked in sessionStorage).
 * The HIGH-priority "1 min left" tip is special: it replaces any current tip
 * and gets a red pulsing treatment.
 */

const GUEST_TIME_LIMIT = 300;          // 5 min, matches GuestSystem
const DISMISSED_KEY = "isaiah_coach_dismissed";
const SHOWN_KEY     = "isaiah_coach_shown";

const loadSet = (key) => {
  try { return new Set(JSON.parse(sessionStorage.getItem(key) || "[]")); } catch { return new Set(); }
};
const saveSet = (key, set) => {
  try { sessionStorage.setItem(key, JSON.stringify([...set])); } catch {}
};

const GuestCoach = () => {
  const navigate = useNavigate();
  const { isGuest, timeLeft, progress, expired } = useGuest();
  const [currentTipId, setCurrentTipId] = useState(null);
  const [dismissedTick, setDismissedTick] = useState(0);  // bump to force re-eval

  const elapsed = GUEST_TIME_LIMIT - timeLeft;
  const stars = progress?.stars || 0;
  const gamesPlayed = progress?.games_played || 0;
  const lessonsStarted = progress?.lessons_started || 0;

  // Compute which tip should currently be showing, if any.
  const activeTip = useMemo(() => {
    if (!isGuest || expired) return null;
    const dismissed = loadSet(DISMISSED_KEY);
    const shown = loadSet(SHOWN_KEY);

    // HIGH PRIORITY: final-minute warning. Always fires if not already dismissed
    // (and ignores the "already shown" rule, we want it to be sticky at the end).
    if (timeLeft > 0 && timeLeft <= 60 && !dismissed.has("final-min")) {
      return {
        id: "final-min",
        priority: 10,
        Icon: Clock,
        title: stars > 0 ? `⏳ 1 minute left!` : `⏳ 1 minute left`,
        body: stars > 0
          ? `Don't lose your ${stars} star${stars === 1 ? "" : "s"}, sign up to save them!`
          : `Sign up now to unlock everything and keep playing.`,
        cta: { label: "Save My Stars", onClick: () => navigate("/signup") },
        variant: "urgent",
      };
    }

    // Lower-priority coaching tips (only if not already shown or dismissed)
    const rules = [
      {
        id: "collect-stars",
        when: elapsed >= 60 && stars === 0,
        Icon: Star,
        title: "Want a star?",
        body: "Tap the shimmering stars around the site to collect them. Every tap counts!",
        variant: "info",
      },
      {
        id: "try-a-game",
        when: elapsed >= 120 && gamesPlayed === 0,
        Icon: Gamepad2,
        title: "Try an Arcade game",
        body: "Win up to 5 stars in one play, fast + fun.",
        cta: { label: "Arcade", onClick: () => navigate("/arcade") },
        variant: "fun",
      },
      {
        id: "try-a-lesson",
        when: elapsed >= 180 && lessonsStarted === 0,
        Icon: BookOpen,
        title: "Complete a lesson = 3 stars",
        body: "Plus you get 2 extra minutes of preview time. Win-win.",
        cta: { label: "Learning Forest", onClick: () => navigate("/learn") },
        variant: "info",
      },
      {
        id: "explore-worlds",
        when: elapsed >= 240 && stars < 5,
        Icon: Compass,
        title: "Explore another world",
        body: "Adventure worlds are full of surprise stars. Go find some!",
        cta: { label: "Adventures", onClick: () => navigate("/adventures") },
        variant: "info",
      },
    ];

    for (const r of rules) {
      if (r.when && !dismissed.has(r.id) && !shown.has(r.id)) {
        return { ...r, priority: 5 };
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, expired, timeLeft, stars, gamesPlayed, lessonsStarted, dismissedTick]);

  // Promote a newly-active tip. We DON'T mark it as "shown" here, that would
  // cause the useMemo to re-evaluate on the next render, see it's shown, and
  // hide the tip immediately (render loop). Instead, "shown" only tracks tips
  // that have been dismissed OR auto-dismissed after a display period.
  useEffect(() => {
    if (!activeTip) { setCurrentTipId(null); return; }
    if (activeTip.id === currentTipId) return;
    setCurrentTipId(activeTip.id);
  }, [activeTip, currentTipId]);

  // Auto-dismiss low-priority tips after 12s of display (don't nag)
  useEffect(() => {
    if (!currentTipId || currentTipId === "final-min") return;
    const t = setTimeout(() => {
      const shown = loadSet(SHOWN_KEY);
      shown.add(currentTipId);
      saveSet(SHOWN_KEY, shown);
      setCurrentTipId(null);
      setDismissedTick((n) => n + 1);
    }, 12000);
    return () => clearTimeout(t);
  }, [currentTipId]);

  const dismiss = () => {
    if (!activeTip) return;
    const dismissed = loadSet(DISMISSED_KEY);
    dismissed.add(activeTip.id);
    saveSet(DISMISSED_KEY, dismissed);
    setCurrentTipId(null);
    setDismissedTick((t) => t + 1);
  };

  if (!isGuest || !activeTip || expired) return null;

  const isUrgent = activeTip.variant === "urgent";
  const variantBg = {
    urgent: "linear-gradient(135deg, #DC2626, #EA580C)",
    info:   "linear-gradient(135deg, #7C3AED, #2563EB)",
    fun:    "linear-gradient(135deg, #F59E0B, #EC4899)",
  }[activeTip.variant] || "linear-gradient(135deg, #7C3AED, #2563EB)";

  const Icon = activeTip.Icon;

  return (
    <div
      className={`fixed bottom-4 right-4 z-[9998] max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-500`}
      data-testid={`guest-coach-${activeTip.id}`}
      role="status"
      aria-live="polite"
    >
      {/* Urgent variant: attention-grabbing pulsing halo behind the card.
          Text stays static so it remains readable while the background breathes. */}
      {isUrgent && (
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background: variantBg,
            filter: "blur(14px)",
            opacity: 0.55,
            animation: "coach-urgent-pulse 1.4s ease-in-out infinite",
          }}
          aria-hidden="true"
        />
      )}
      <div
        className="relative rounded-2xl px-4 py-3.5 flex items-start gap-3 shadow-2xl pointer-events-auto"
        style={{
          background: variantBg,
          boxShadow: "0 15px 50px -10px rgba(0,0,0,0.5)",
          ...(isUrgent ? { animation: "coach-urgent-border 1.4s ease-in-out infinite" } : {}),
        }}
      >
        <div className="w-9 h-9 rounded-full bg-white/25 border border-white/35 flex items-center justify-center shrink-0">
          <Icon size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-fredoka font-bold text-sm leading-tight">{activeTip.title}</p>
          <p className="text-white/90 text-[11px] leading-snug mt-0.5">{activeTip.body}</p>
          {activeTip.cta && (
            <button
              onClick={activeTip.cta.onClick}
              className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/25 hover:bg-white/35 border border-white/40 text-white text-[11px] font-fredoka font-bold transition-all"
              data-testid={`guest-coach-cta-${activeTip.id}`}
            >
              {isUrgent ? <Lock size={11} /> : <Rocket size={11} />}
              {activeTip.cta.label}
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          className="shrink-0 p-1 rounded-md hover:bg-white/20 text-white/70 hover:text-white transition-all"
          aria-label="Dismiss coach tip"
          data-testid={`guest-coach-dismiss-${activeTip.id}`}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default GuestCoach;
